import {Injectable} from '@angular/core';
import {Action, State, StateContext, Store} from '@ngxs/store';
import {Subject} from 'rxjs';
import { CommonService } from 'src/app/services/common.service';
import {NetworkConfigurationMetaData} from '../../models/network/network-query-result';
import {NetworkSignalrType, PanelTestSignalR} from '../../models/network/network.interface';
import {NetworksService} from '../../services/networks.service';
import {SharedState} from '../shared/shared.state';
import {
  GetNetworksConfiguration,
  LoadMoreNetworksResults,
  LoadNetworks,
  SelectNetwork,
  SetNetworksFilter,
  TestController,
  UpdateNetworks,
  SetIsPrinting
} from './networks.actions';
import {NetworksStateModel} from './networks.model';

@State<NetworksStateModel>({
  name: 'networks',
  defaults: {
    networks: null,
    selectedNetwork: null,
    totalNetworksCount: 0,
    currentPage: 0,
    isLoadingData: false,
    networksFilter: {
      search: null,
      filter: {
        status: null
      }
    },
    configuration: null,
    isTouched: false,
    panelTestResultsSubject$: new Subject<PanelTestSignalR>(),
    isPrinting: false
  }
})

@Injectable()
export class NetworksState {
  constructor(private networksService: NetworksService, private store: Store, private commonService: CommonService) {
  }

  networkTypesDisplayTexts = {
    0:'Serial',
    1:'TCP / IP',
    2:'Modem'
  };

  @Action(LoadNetworks)
  async loadNetworks(ctx: StateContext<NetworksStateModel>) {
    ctx.patchState({isLoadingData: true});
    let networks = await this.networksService.getNetworks(1, 100, ctx.getState().networksFilter);
    if(networks){
      if (networks.results) {
        networks.results.sort(this.commonService.compareByNameAscFn);
        networks.results?.forEach(n => {
          n.typeName = this.networkTypesDisplayTexts?.[n?.typeId] || '';
        });
      }
      ctx.patchState({
        isLoadingData: false,
        networks: networks.results,
        totalNetworksCount: networks.totalCount,
        currentPage: 0,
        isTouched: true
      });
    }
  }

  @Action(SetNetworksFilter)
  async setNetworksFilter(ctx: StateContext<NetworksStateModel>, action: SetNetworksFilter) {
    ctx.patchState({networksFilter: {...action.filter}});
  }

  @Action(LoadMoreNetworksResults)
  async loadMoreResults(ctx: StateContext<NetworksStateModel>, action: LoadMoreNetworksResults) {
    if (action.resetChunk) {
      ctx.patchState({currentPage: 0});
    }
    let {currentPage, networks} = ctx.getState();
    let nextPage = currentPage;
    const chunkSize = this.store.selectSnapshot(SharedState.resultsChunkSize);
    ctx.patchState({isLoadingData: true});
    let networkResults = await this.networksService.getNetworks(++nextPage, chunkSize, ctx.getState().networksFilter);
    ctx.patchState({isLoadingData: false});
    if(networkResults){
      const networkList = action.resetChunk ? [] : networks;
      if (networkResults.results) {
        networkResults.results.sort(this.commonService.compareByNameAscFn);
        networkResults.results?.forEach(n => {
          n.typeName = this.networkTypesDisplayTexts?.[n?.typeId] || '';
        });
      }
      ctx.patchState({
        isTouched: true,
        networks: [...networkList, ...networkResults.results],
        totalNetworksCount: networkResults.totalCount
      });

      if (networkList.length !== ctx.getState().networks.length) {
        ctx.patchState({
          currentPage: nextPage
        });
      }
    }
  }

  @Action(GetNetworksConfiguration)
  async getNetworksConfiguration(ctx: StateContext<NetworksStateModel>) {
    let configuration: NetworkConfigurationMetaData = await this.networksService.getConfiguration();
    if (configuration) {
      configuration.panelStatus = configuration.panelStatus.map(item => {
      if ( item.description.includes('Initialize')) {
        return {
          id: item.id,
          description: 'Initialized'
        };
      } else {
        return item;
      }
     });
      ctx.patchState({configuration});
    }
  }

  @Action(SelectNetwork)
  async selectNetwork(ctx: StateContext<NetworksStateModel>, action: SelectNetwork) {
    if (action.network && action.network.id) {
      ctx.patchState({isLoadingData: true});
      this.networksService.getNetworkDetailsById(action.network.id).then(n => {
        if(n){
          let selectedNetwork = n.network;
          selectedNetwork.typeId = action.network.typeId;
          selectedNetwork.typeName = action.network.typeName;

          ctx.patchState({selectedNetwork});
          ctx.patchState({isLoadingData: false});
        }
      });
    } else {
      ctx.patchState({selectedNetwork: null});
    }
  }

  @Action(TestController)
  async testController(ctx: StateContext<NetworksStateModel>, action: TestController) {
    const testResult = await this.networksService.testController({panelId: action.controller.id});
    if (testResult && action.networkSubscription$.observers) {
      action.networkSubscription$.next(testResult);
    }
  }

  @Action(UpdateNetworks)
  async updateNetworks(ctx: StateContext<NetworksStateModel>, action: UpdateNetworks) {
    if (action?.networkUpdate?.type === NetworkSignalrType.Network) {
      const {networks, selectedNetwork} = ctx.getState();
      if (networks) {
        const networkItems = networks.filter(f => f.id === action.networkUpdate.id);
        if (networkItems.length > 0) {
          const selectedIndex = networks.indexOf(networkItems[0]);
          networks[selectedIndex] = {
            ...networks[selectedIndex],
            controllersStatusId: action.networkUpdate.statusId,
            name: action.networkUpdate.description
          };

          ctx.patchState({
            networks: [...networks]
          });
        }
      }
      if (selectedNetwork) {
        selectedNetwork.networkStatusId = action.networkUpdate.statusId;
        ctx.patchState({selectedNetwork: {...selectedNetwork}});
      }
    }
    if (action?.networkUpdate.type === NetworkSignalrType.Panel) {
      const {selectedNetwork} = ctx.getState();
      if (selectedNetwork?.id) {
        const {controllers} = ctx.getState().selectedNetwork;
        const filteredControllers = controllers.filter(p => p.id === action.networkUpdate.id);
        if (filteredControllers.length > 0) {
          const controllerIndex = controllers.indexOf(filteredControllers[0]);
          selectedNetwork.controllers[controllerIndex].statusId = action.networkUpdate.statusId;
          ctx.patchState({selectedNetwork: {...selectedNetwork}});
        }
      }
    }
  }
  @Action(SetIsPrinting)
  async setIsPrinting(ctx: StateContext<NetworksStateModel>, action: SetIsPrinting) {
    ctx.patchState({isPrinting:  action.isPrinting});
  }
}
